import { ContentHeader } from 'components/ui/layout/ContentHeader';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { useUI } from 'contexts/UiContext';
import { PermissionActions, PermissionModules } from 'types/rolesTypes';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { DataSourcesListView } from 'types/dataSourcesTypes';
import icons from 'components/darwin-ui/Icon/Icons';
import useTableControls from 'hooks/useTableControls';
import { useGetAllDataSourcesByCompany } from 'api/services/dataSourceService';
import { DarwinListView } from 'components/darwin-ui';
import { getDataSourceColumns } from './columns';
import { DeleteDataSourceModal } from 'components/ui/dataSources/DeleteDataSourceModal.tsx';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { CreateEditOrViewDataSourceModal } from 'components/ui/dataSources/CreateOrEditDataSourceModal.tsx';
import { generatePath, useNavigate } from 'react-router-dom';
import { ROUTES } from 'routes/routes';

export type ColumnAction = {
  key: string;
  name?: keyof typeof icons;
  label?: string;
  onClick?: (dataSource: DataSourcesListView) => void;
};

export const DataSources = () => {
  const { t } = useTranslation();
  const { selectedCompany } = useUI();
  const { openNotification } = useFloatNotification();

  const navigate = useNavigate();

  const {
    pageSize,
    currentPage,
    onPageSizeChange,
    setCurrentPage,
    searchQuery,
    setSearchQuery,
    sortOrder,
    onTableChange,
  } = useTableControls();

  const [dataSource, setDataSource] = useState<DataSourcesListView>();
  const [createDataSource, setCreateDataSource] = useState<boolean>(false);
  const [editDataSource, setEditDataSource] = useState<boolean>(false);
  const [viewDataSource, setViewDataSource] = useState<boolean>(false);
  const [deleteDataSource, setDeleteDataSource] = useState<boolean>(false);

  const checkPermission = usePermissionChecker();

  const viewDataSourcePermission = checkPermission(
    PermissionModules.DataSources,
    PermissionActions.Read
  );
  const createDataSourcePermission = checkPermission(
    PermissionModules.DataSources,
    PermissionActions.Create
  );
  const updateDataSourcePermission = checkPermission(
    PermissionModules.DataSources,
    PermissionActions.Update
  );
  const deleteDataSourcePermission = checkPermission(
    PermissionModules.DataSources,
    PermissionActions.Delete
  );

  const {
    data: dataSourceData,
    isLoading: isLoadingDataSourceData,
    isFetching: isFetchingDataSourceData,
    isPending: isPendingDataSourceData,
    error: dataSourceError,
  } = useGetAllDataSourcesByCompany({
    companyId: selectedCompany || '',
    page: currentPage,
    limit: pageSize,
    query: searchQuery,
    sortOrder,
  });

  const tableData = dataSourceData?.data ?? [];

  const availableActions: Array<{
    permission: boolean | null;
    action: ColumnAction;
  }> = [
    {
      permission: updateDataSourcePermission,
      action: {
        key: 'edit',
        name: 'edit',
        label: t('list-view.action-items.edit'),
        onClick: dataSource => {
          setDataSource(dataSource);
          setEditDataSource(true);
        },
      },
    },
    {
      permission: viewDataSourcePermission,
      action: {
        key: 'view',
        name: 'view',
        label: t('list-view.action-items.view'),
        onClick: dataSource => {
          setDataSource(dataSource);
          setViewDataSource(true);
        },
      },
    },
    {
      permission: deleteDataSourcePermission,
      action: {
        key: 'delete',
        name: 'delete',
        label: t('list-view.action-items.delete'),
        onClick: dataSource => {
          setDataSource(dataSource);
          setDeleteDataSource(true);
        },
      },
    },
  ];

  useEffect(() => {
    if (dataSourceError?.message) {
      openNotification(
        'Error',
        t('data-sources.data-source-error-message'),
        'error'
      );
    }
  }, [dataSourceError?.message, openNotification, t]);

  const actions: ColumnAction[] = availableActions
    .filter(item => Boolean(item.permission))
    .map(item => item.action);

  const columns = getDataSourceColumns(t, actions, searchQuery);

  const handleRowClick = useCallback(
    (dataSource: DataSourcesListView) => {
      if (updateDataSourcePermission) {
        navigate(generatePath(ROUTES.DATA_SOURCES.EDIT, { id: dataSource.id }));
      }
    },
    [updateDataSourcePermission, navigate]
  );

  const isLoading =
    isLoadingDataSourceData ||
    isFetchingDataSourceData ||
    isPendingDataSourceData ||
    viewDataSourcePermission === null;

  const createNewDataSource = () => {
    setCreateDataSource(true);
  };

  const onCancelModal = useCallback(() => {
    setCreateDataSource(false);
    setDeleteDataSource(false);
    setEditDataSource(false);
    setViewDataSource(false);
    setDataSource(undefined);
  }, []);

  const mode = createDataSource
    ? 'creating'
    : editDataSource
    ? 'editing'
    : 'viewing';

  return (
    <>
      <ContentHeader
        title={t('data-sources.title')}
        {...(createDataSourcePermission && {
          onSave: createNewDataSource,
          onSaveTitle: t('data-sources.data-source-modal.add-button'),
        })}
      />

      <DarwinListView<DataSourcesListView>
        data={tableData}
        columns={columns}
        isLoading={isLoading}
        pagination={{
          total: dataSourceData?.metadata?.total,
          pageSize,
          currentPage,
          onPageChange: setCurrentPage,
          onPageSizeChange,
        }}
        header={{
          setSearchQuery,
          searchPlaceholder: t('data-sources.data-source-search-placeholder'),
        }}
        table={{
          onChange: onTableChange,
        }}
        behavior={{
          onRowClick: handleRowClick,
        }}
      />

      <CreateEditOrViewDataSourceModal
        mode={mode}
        dataSourceData={dataSource}
        onCancel={onCancelModal}
        open={!!(editDataSource || createDataSource || viewDataSource)}
      />

      <DeleteDataSourceModal
        open={!!deleteDataSource}
        onCancel={onCancelModal}
        dataSourceId={dataSource?.id.toString()}
        name={dataSource?.name}
      />
    </>
  );
};
